import { decodeJwt } from '../jwt/verifyJwt';
import type { AuthenticateRequestOptions } from '../tokens/types';
import type { MachineTokenType } from './tokenTypes';
import { TokenType } from './tokenTypes';

export const M2M_TOKEN_PREFIX = 'mt_';
export const M2M_SUBJECT_PREFIX = 'mch_';
export const OAUTH_TOKEN_PREFIX = 'oat_';
export const API_KEY_PREFIX = 'ak_';

// Token-category tag in the protected JOSE header of instance-signed M2M JWTs,
// used to distinguish them from other JWT classes signed by the same instance
// key. Kept in sync with clerk_go (pkg/jwt) and cloudflare-workers.
export const JWT_CATEGORY_M2M_TOKEN = 'cl_B7d4PD333AAA';

const MACHINE_TOKEN_PREFIXES = [M2M_TOKEN_PREFIX, OAUTH_TOKEN_PREFIX, API_KEY_PREFIX] as const;

export const JwtFormatRegExp = /^[a-zA-Z0-9\-_]+\.[a-zA-Z0-9\-_]+\.[a-zA-Z0-9\-_]+$/;

export function isJwtFormat(token: string): boolean {
  return JwtFormatRegExp.test(token);
}

/**
 * Valid OAuth 2.0 JWT access token type values per RFC 9068.
 * @see https://www.rfc-editor.org/rfc/rfc9068.html#section-2.1
 */
export const OAUTH_ACCESS_TOKEN_TYPES = ['at+jwt', 'application/at+jwt'];

/**
 * Checks if a token is an OAuth 2.0 JWT access token.
 * Validates the JWT format and verifies the header 'typ' field matches RFC 9068 values.
 *
 * @param token - The token string to check
 * @returns true if the token is a valid OAuth JWT access token
 * @see https://www.rfc-editor.org/rfc/rfc9068.html#section-2.1
 */
export function isOAuthJwt(token: string): boolean {
  if (!isJwtFormat(token)) {
    return false;
  }
  try {
    const { data, errors } = decodeJwt(token);
    return (
      !errors &&
      !!data &&
      OAUTH_ACCESS_TOKEN_TYPES.includes(data.header.typ as (typeof OAUTH_ACCESS_TOKEN_TYPES)[number])
    );
  } catch {
    return false;
  }
}

/**
 * Checks if a token is an M2M JWT token.
 * Validates the JWT format and verifies the payload 'sub' field starts with 'mch_'.
 *
 * @param token - The token string to check
 * @returns true if the token is a valid M2M JWT token
 */
export function isM2MJwt(token: string): boolean {
  if (!isJwtFormat(token)) {
    return false;
  }
  try {
    const { data, errors } = decodeJwt(token);
    return !errors && !!data && typeof data.payload.sub === 'string' && data.payload.sub.startsWith(M2M_SUBJECT_PREFIX);
  } catch {
    return false;
  }
}

/**
 * Checks if a token is a machine JWT (OAuth JWT or M2M JWT).
 * Useful for rejecting machine JWTs when expecting session tokens.
 *
 * @param token - The token string to check
 * @returns true if the token is an OAuth or M2M JWT
 */
export function isMachineJwt(token: string): boolean {
  return isOAuthJwt(token) || isM2MJwt(token);
}

/**
 * Checks if a token is a machine token by looking at its prefix.
 *
 * @remarks
 * In the future, this will support custom prefixes that can be prepended to the base prefixes
 * (e.g. "org_a_m2m_", "org_a_oauth_access_", "org_a_api_key_")
 *
 * @param token - The token string to check
 * @returns true if the token starts with a recognized machine token prefix
 */
export function isMachineTokenByPrefix(token: string): boolean {
  return MACHINE_TOKEN_PREFIXES.some(prefix => token.startsWith(prefix));
}

/**
 * Checks if a token is a machine token by looking at its prefix or if it's an OAuth/M2M JWT.
 *
 * @param token - The token string to check
 * @returns true if the token is a machine token
 */
export function isMachineToken(token: string): boolean {
  return isMachineTokenByPrefix(token) || isOAuthJwt(token) || isM2MJwt(token);
}

/**
 * Gets the specific type of machine token based on its prefix or JWT claims.
 *
 * @remarks
 * In the future, this will support custom prefixes that can be prepended to the base prefixes
 * (e.g. "org_a_m2m_", "org_a_oauth_access_", "org_a_api_key_")
 *
 * @param token - The token string to check
 * @returns The specific MachineTokenType
 * @throws Error if the token doesn't match any known machine token type
 */
export function getMachineTokenType(token: string): MachineTokenType {
  // M2M: prefix OR JWT with mch_ subject
  if (token.startsWith(M2M_TOKEN_PREFIX) || isM2MJwt(token)) {
    return TokenType.M2MToken;
  }

  // OAuth: prefix OR JWT with at+jwt typ
  if (token.startsWith(OAUTH_TOKEN_PREFIX) || isOAuthJwt(token)) {
    return TokenType.OAuthToken;
  }

  if (token.startsWith(API_KEY_PREFIX)) {
    return TokenType.ApiKey;
  }

  throw new Error('Unknown machine token type');
}

/**
 * Check if a token type is accepted given a requested token type or list of token types.
 *
 * @param tokenType - The token type to check (can be null if the token is invalid)
 * @param acceptsToken - The requested token type or list of token types
 * @returns true if the token type is accepted
 */
export const isTokenTypeAccepted = (
  tokenType: TokenType | null,
  acceptsToken: NonNullable<AuthenticateRequestOptions['acceptsToken']>,
): boolean => {
  if (!tokenType) {
    return false;
  }

  if (acceptsToken === 'any') {
    return true;
  }

  const tokenTypes = Array.isArray(acceptsToken) ? acceptsToken : [acceptsToken];
  return tokenTypes.includes(tokenType);
};

const MACHINE_TOKEN_TYPES = new Set<string>([TokenType.ApiKey, TokenType.M2MToken, TokenType.OAuthToken]);

/**
 * Checks if a token type string is a machine token type (api_key, m2m_token, or oauth_token).
 *
 * @param type - The token type string to check
 * @returns true if the type is a machine token type
 */
export function isMachineTokenType(type: string): type is MachineTokenType {
  return MACHINE_TOKEN_TYPES.has(type);
}
